import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/context/LanguageContext';

import { StatsCard } from '@/components/dashboard/StatsCard';
import { PriceChart } from '@/components/dashboard/PriceChart';
import { PortfolioWidget } from '@/components/dashboard/PortfolioWidget';
import { MarketTable } from '@/components/dashboard/MarketTable';
import { TransactionHistory } from '@/components/dashboard/TransactionHistory';
import { AIInsights } from '@/components/dashboard/AIInsights';
import { AlertsWidget } from '@/components/dashboard/AlertsWidget';

// Widget definitions
const WIDGET_COMPONENTS = {
  'stats': () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      <StatsCard
        title="Portfolio Value"
        value="$124,567"
        change={12.34}
        changeType="positive"
        icon="fa-solid fa-wallet"
        subtitle="Updated 2m ago"
      />
      <StatsCard
        title="24h Profit/Loss"
        value="+$2,456"
        change={5.67}
        changeType="positive"
        icon="fa-solid fa-chart-line"
        subtitle="Top: BTC +2.3%"
      />
      <StatsCard
        title="Total Trades"
        value="156"
        change={8}
        changeType="positive"
        icon="fa-solid fa-arrow-right-arrow-left"
        subtitle="23 this week"
      />
      <StatsCard
        title="Active Alerts"
        value="7"
        icon="fa-solid fa-bell"
        subtitle="3 triggered today"
      />
    </div>
  ),
  'chart': () => (
    <PriceChart
      coin="Bitcoin"
      symbol="BTC"
      currentPrice={67234.56}
      priceChange={2.34}
    />
  ),
  'portfolio': () => <PortfolioWidget />,
  'ai-insights': () => <AIInsights />,
  'alerts': () => <AlertsWidget />,
  'market': () => <MarketTable compact />,
  'transactions': () => <TransactionHistory maxItems={5} />,
};

const WIDGET_INFO = {
  'stats': { title: 'Stats Cards', icon: 'fa-solid fa-chart-bar', colSpan: 'col-span-full' },
  'chart': { title: 'Price Chart', icon: 'fa-solid fa-chart-line', colSpan: 'lg:col-span-2' },
  'portfolio': { title: 'Portfolio', icon: 'fa-solid fa-briefcase', colSpan: 'lg:col-span-1' },
  'ai-insights': { title: 'AI Insights', icon: 'fa-solid fa-robot', colSpan: 'lg:col-span-1' },
  'alerts': { title: 'Price Alerts', icon: 'fa-solid fa-bell', colSpan: 'lg:col-span-1' },
  'market': { title: 'Market Overview', icon: 'fa-solid fa-table', colSpan: 'lg:col-span-1' },
  'transactions': { title: 'Recent Transactions', icon: 'fa-solid fa-clock-rotate-left', colSpan: 'lg:col-span-1' },
};

const DEFAULT_LAYOUT = ['stats', 'chart', 'portfolio', 'ai-insights', 'alerts', 'market', 'transactions'];
const STORAGE_KEY = 'crypto-dashboard-layout';
const HIDDEN_KEY = 'crypto-dashboard-hidden';

// Sortable Widget Wrapper
const SortableWidget = ({ id, isEditMode, isHidden, onToggleVisibility }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isEditMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const widgetInfo = WIDGET_INFO[id];
  const WidgetComponent = WIDGET_COMPONENTS[id];

  if (isHidden && !isEditMode) return null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        widgetInfo.colSpan,
        "relative group",
        isEditMode && "cursor-move",
        isHidden && isEditMode && "opacity-40"
      )}
      data-testid={`widget-${id}`}
    >
      {isEditMode && (
        <div className="absolute -top-2 -right-2 z-10 flex items-center gap-1">
          <Button
            variant="secondary"
            size="icon"
            className="h-7 w-7 rounded-full shadow-md"
            onClick={() => onToggleVisibility(id)}
          >
            <i className={cn(
              "text-xs",
              isHidden ? "fa-solid fa-eye-slash text-muted-foreground" : "fa-solid fa-eye text-primary"
            )}></i>
          </Button>
          <div
            {...attributes}
            {...listeners}
            className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center cursor-grab active:cursor-grabbing shadow-md"
          >
            <i className="fa-solid fa-grip-vertical text-xs"></i>
          </div>
        </div>
      )}
      {isEditMode && (
        <div className="absolute inset-0 border-2 border-dashed border-primary/30 rounded-lg pointer-events-none z-0" />
      )}
      <WidgetComponent />
    </div>
  );
};

// Widget Preview for Drag Overlay
const WidgetPreview = ({ id }) => {
  const widgetInfo = WIDGET_INFO[id];
  
  return (
    <Card className="w-64 shadow-2xl border-2 border-primary">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <i className={cn(widgetInfo.icon, "text-primary")}></i>
        </div>
        <div>
          <p className="font-medium">{widgetInfo.title}</p>
          <p className="text-xs text-muted-foreground">Dragging...</p>
        </div>
      </CardContent>
    </Card>
  );
};

export const Dashboard = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [layout, setLayout] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_LAYOUT;
  });
  const [hiddenWidgets, setHiddenWidgets] = useState(() => {
    const saved = localStorage.getItem(HIDDEN_KEY);
    return saved ? JSON.parse(saved) : [];
  });
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Save layout to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  }, [layout]);

  // Save hidden widgets to localStorage
  useEffect(() => {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(hiddenWidgets));
  }, [hiddenWidgets]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id) {
      setLayout((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleToggleVisibility = (widgetId) => {
    setHiddenWidgets(prev => 
      prev.includes(widgetId) 
        ? prev.filter(id => id !== widgetId)
        : [...prev, widgetId]
    );
  };

  const resetLayout = () => {
    setLayout(DEFAULT_LAYOUT);
    setHiddenWidgets([]);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(HIDDEN_KEY);
  };

  const visibleCount = layout.filter(id => !hiddenWidgets.includes(id)).length;
  const { t } = useLanguage();

  return (
    <div className="p-4 lg:p-6 space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Page Header with Edit Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">{t('dashboard')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('welcomeBack')}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isEditMode && (
            <Badge variant="outline" className="text-xs">
              {visibleCount} {t('of')} {layout.length} {t('widgets')}
            </Badge>
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="customize-btn">
                <i className="fa-solid fa-sliders mr-2"></i>
                {t('customize')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="p-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-mode" className="text-sm">{t('editMode')}</Label>
                  <Switch
                    id="edit-mode"
                    checked={isEditMode}
                    onCheckedChange={setIsEditMode}
                    data-testid="edit-mode-switch"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('dragWidgets')}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={resetLayout}>
                <i className="fa-solid fa-rotate-left mr-2"></i>
                {t('resetLayout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {isEditMode && (
            <Button 
              size="sm" 
              onClick={() => setIsEditMode(false)}
              data-testid="done-editing-btn"
            >
              <i className="fa-solid fa-check mr-2"></i>
              {t('done')}
            </Button>
          )}
        </div>
      </div>

      {/* Edit Mode Banner */}
      {isEditMode && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <i className="fa-solid fa-arrows-up-down-left-right text-primary"></i>
          </div>
          <div>
            <p className="font-medium text-sm">{t('customizeDashboard')}</p>
            <p className="text-xs text-muted-foreground">
              {t('dragToReorder')}
            </p>
          </div>
        </div>
      )}

      {/* Widget Grid with DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={layout} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
            {layout.map((widgetId) => (
              <SortableWidget
                key={widgetId}
                id={widgetId}
                isEditMode={isEditMode}
                isHidden={hiddenWidgets.includes(widgetId)}
                onToggleVisibility={handleToggleVisibility}
              />
            ))}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeId ? <WidgetPreview id={activeId} /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default Dashboard;
